import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabaseMutationFn, useSupabaseTypedClient } from 'hooks/reactQuery';
import { useToastWrapper } from 'hooks/useToastWrapper';
import { useNavigate } from 'react-router-dom';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import { EdgeFunctionName } from 'types/edgeFunctions';
import { ERoute, TanstackQueryName } from 'types/frontend';

interface CreateRoastParams {
  url: string;
}

interface CreateRoastResponse {
  roast_id: string;
  slug: string;
}

export const useCreateRoastMutation = () => {
  const supabase = useSupabaseTypedClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { id: userId } = useCurrentUser();
  const { toastSuccess, toastError } = useToastWrapper();

  return useMutation<CreateRoastResponse, unknown, CreateRoastParams>({
    mutationFn: supabaseMutationFn(({ url }) =>
      supabase.functions.invoke<CreateRoastResponse>(EdgeFunctionName.Roast, {
        body: { type: 'create', url },
      })
    ),
    onSuccess: (data) => {
      toastSuccess('Roast submitted! AI is analyzing your project.');
      queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetRoasts, userId] });
      queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetAllRoasts] });
      queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetProfileRoasts, userId] });
      navigate(`${ERoute.Roasts}/${data.roast_id}`);
    },
    onError: (error) => {
      const msg = typeof error === 'string' ? error : error instanceof Error ? error.message : 'Failed to submit roast';
      toastError(msg || 'Failed to submit roast');
    },
  });
};
